import React, { useEffect, useRef } from "react";
import { IndustryCard } from "../../components/library/cards/industy-card";
import { ExploreIcon } from "../../assets/images/svgExports";
import Indu1 from "../../assets/images/indu1.png";
import Indu2 from "../../assets/images/indu2.png";
import Indu3 from "../../assets/images/indu3.png";
import Indu4 from "../../assets/images/indu4.png";
import Indu5 from "../../assets/images/indu5.png";
import Indu6 from "../../assets/images/indu6.png";
import Indu7 from "../../assets/images/indu7.png";
import Indu8 from "../../assets/images/indu8.png";
import Layout from "../../components/layout/layout";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const stats = [
  { value: "10+", label: "Enterprise Clients" },
  { value: "20M+", label: "Consumers Reached" },
  { value: "3+", label: "African Markets" },
  { value: "99.9%", label: "Platform Uptime" },
];

const items = [
  {
    title: "Retail & E-Commerce",
    description:
      "Turn browsers into buyers with personalized rewards and smart SMS campaigns that drive repeat purchases.",
    imageSrc: Indu1,
    icon: ExploreIcon,
  },
  {
    title: "Telecommunications",
    description:
      "Keep customers connected with data-driven engagement that reduces churn and grows revenue per user.",
    imageSrc: Indu2,
    icon: ExploreIcon,
  },
  {
    title: "Healthcare & Wellness",
    description:
      "Build healthier relationships with appointment reminders and wellness rewards that keep patients engaged.",
    imageSrc: Indu3,
    icon: ExploreIcon,
  },
  {
    title: "Financial Services",
    description:
      "Drive transactions and retention with smart campaigns and personalized financial engagement.",
    imageSrc: Indu4,
    icon: ExploreIcon,
  },
  {
    title: "Transportation & Logistics",
    description:
      "Build rider loyalty with cashback, referrals, and promotions that keep them coming back for every trip.",
    imageSrc: Indu5,
    icon: ExploreIcon,
  },
  {
    title: "Hospitality & Travel",
    description:
      "Create memorable experiences with VIP perks and rewards that turn guests into loyal brand ambassadors.",
    imageSrc: Indu6,
    icon: ExploreIcon,
  },
  {
    title: "FMCG & Consumer Goods",
    description:
      "Connect directly with consumers through mobile engagement that drives trial and builds lasting brand loyalty.",
    imageSrc: Indu7,
    icon: ExploreIcon,
  },
  {
    title: "Media & Entertainment",
    description:
      "Keep audiences hooked with personalized content and fan rewards that reduce subscription churn.",
    imageSrc: Indu8,
    icon: ExploreIcon,
  },
];

const IndustryPage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const mm = gsap.matchMedia();

    mm.add("(prefers-reduced-motion: reduce)", () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
      gsap.globalTimeline.clear();
    });

    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const ctx = gsap.context(() => {
        gsap
          .timeline({ defaults: { ease: "power3.out", duration: 0.85 } })
          .from(".loyalty-hero-heading button", { y: 16, opacity: 0 })
          .from(".loyalty-hero-heading h1", { y: 22, opacity: 0 }, "-=0.55")
          .from(".loyalty-hero-heading p", { y: 16, opacity: 0 }, "-=0.55");

        gsap.fromTo(
          ".loyalty-hero-mission",
          { y: 26, opacity: 0, scale: 0.98 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: {
              trigger: ".loyalty-hero-mission",
              start: "top 82%",
              toggleActions: "play none none reverse",
            },
          },
        );

        const cards = gsap.utils.toArray<HTMLElement>("[data-industry-card]");
        if (cards.length) {
          ScrollTrigger.batch(cards, {
            start: "top 88%",
            onEnter: (batch) =>
              gsap.fromTo(
                batch,
                { opacity: 0, y: 22, scale: 0.985 },
                {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  duration: 0.7,
                  ease: "power3.out",
                  stagger: 0.08,
                  overwrite: true,
                },
              ),
            onLeaveBack: (batch) =>
              gsap.to(batch, {
                opacity: 0,
                y: 22,
                scale: 0.985,
                duration: 0.35,
                ease: "power2.out",
                overwrite: true,
              }),
          });
        }

        gsap.fromTo(
          "[data-anim='stats']",
          { y: 28, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: {
              trigger: "[data-anim='stats']",
              start: "top 82%",
              toggleActions: "play none none reverse",
            },
          },
        );

        const statItems = gsap.utils.toArray<HTMLElement>("[data-stat]");
        if (statItems.length) {
          gsap.from(statItems, {
            opacity: 0,
            y: 14,
            duration: 0.65,
            ease: "power2.out",
            stagger: 0.1,
            scrollTrigger: {
              trigger: "[data-stagger='stats']",
              start: "top 86%",
              toggleActions: "play none none reverse",
            },
          });
        }
      }, pageRef);

      return () => ctx.revert();
    });

    return () => mm.revert();
  }, []);

  return (
    <Layout
      footerData={{
        headerText: "Ready to Transform Your Industry?",
        bodyText:
          "Join leading brands in your industry who are already using Bridgetune to engage, retain, and reward their customers.",
      }}
    >
      <div ref={pageRef}>
        <section
          className="
            relative bg-[#2C73DB] mb-[500px] md:mb-[350px] 
            rounded-bl-[80px] rounded-br-[80px]
            md:rounded-bl-[160px] md:rounded-br-[160px]
            lg:rounded-bl-[200px] lg:rounded-br-[200px]
            text-white pt-32 flex items-end justify-center
          "
        >
          <div className="max-w-5xl relative px-4 sm:px-6">
            <div className="loyalty-hero-heading text-center mb-[12%]">
              <div className="inline-flex items-center bg-[#ffffff33] text-xs font-[300] py-2 px-3 rounded-full mb-3 border border-[#fff] shadow-[0_0_18px_rgba(59,130,246,0.25)]">
                Industries We Serve
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-medium leading-tight mb-4 px-2">
                Built for Every Industry Across Africa
              </h1>

              <p className="text-sm md:text-base text-blue-100 max-w-2xl mx-auto leading-relaxed">
                From retail to healthcare, our technology-powered platform helps
                brands in every sector engage customers, drive retention, and
                build lasting loyalty.
              </p>
            </div>
          </div>

          <div
            className="
              loyalty-hero-mission
              absolute left-1/2 -translate-x-1/2
              top-[92%]
              w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] max-w-3xl
            "
          >
            <div className="bg-blue-50 rounded-[60px] px-5 md:px-4 py-16">
              <div className="flex flex-col items-center text-center gap-4 md:gap-5">
                <div>
                  <h2 className="text-lg md:text-3xl font-medium mb-2 text-[#1e1e1e]">
                    Transforming Every Industry
                  </h2>

                  <p className="text-xs md:text-sm text-[#8e8e8e] max-w-6xl mx-auto mb-6 leading-relaxed">
                    Across Africa's diverse business landscape, our platform
                    helps brands in retail, telecommunications, healthcare, and
                    beyond build deeper customer connections. From urban centers
                    to remote communities, we adapt to your unique challenges,
                    turning campaigns into relationships, transactions into
                    experiences, and engagement into lasting loyalty that drives
                    growth wherever your customers are.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="my-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <section
              className="
                grid gap-6
                sm:grid-cols-2
                lg:grid-cols-12
                items-stretch
              "
            >
              {items.map((it, idx) => (
                <IndustryCard
                  key={it.title}
                  title={it.title}
                  description={it.description}
                  imageSrc={it.imageSrc}
                  onClick={() => {}}
                  data-industry-card
                  className={[
                    "sm:col-span-1 lg:col-span-4",
                    idx === 6 ? "lg:col-start-3" : "",
                    idx === 7 ? "lg:col-start-7" : "",
                  ].join(" ")}
                />
              ))}
            </section>
          </div>
        </div>

        <section
          data-anim="stats"
          className="max-w-full md:max-w-6xl mx-auto bg-blue-50 px-6 mb-44 rounded-[60px] py-14"
        >
          <div className="text-center">
            <h2 className="text-lg text-[#1E1E1E] md:text-3xl font-medium mb-2">
              Trusted Across Industries
            </h2>
            <p className="mt-3 text-sm sm:text-base text-[#8e8e8e]">
              Leading brands across Africa rely on our platform to transform
              customer engagement
            </p>
          </div>

          <div
            data-stagger="stats"
            className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center mt-12"
          >
            {stats.map((industries, idx) => (
              <div
                key={idx}
                data-stat
                className="w-full max-w-full md:max-w-[250px] px-6 text-center"
              >
                <h3 className="text-2xl sm:text-[28px] font-extrabold tracking-tight text-blue-500">
                  {industries.value}
                </h3>
                <h3 className="mt-2 text-md whitespace-nowrap font-[100] text-[#1e1e1e]">
                  {industries.label}
                </h3>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default IndustryPage;
